/* eslint-disable max-params */
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';

import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  WcgButtonComponent,
  WcgDialogComponent,
  WcgMenuOption,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';
import { SingleSelectDialogData } from '../../../types';
import {
  organizationRoleHints,
  setFormControlValidators,
} from '@data-access/shared';
import { DIALOG_MODES } from '@erm/cluster';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgButtonComponent,
    WcgDialogComponent,
    WcgUniSelectComponent,
  ],
  selector: 'wcg-single-select',
  styleUrls: ['./single-select.dialog.component.css'],
  template: `
    @defer (when singleSelectForm && data) {
      <form [formGroup]="singleSelectForm">
        <wcg-dialog
          [title]="data.title"
          [description]="data.description"
          [size]="data.size"
          data-testid="single-text-dialog"
        >
          <div
            data-testid="single-select-dialog__content"
            class="w-full flex mt-8 single-select-dialog__content"
            content
          >
            @if (data.mode !== 'delete') {
              @let hintText =
                data.label.toLowerCase() === 'organization role'
                  ? organizationRoleHintMapper.get(
                      singleSelectForm.controls.selectControl.value?.label!.toLowerCase()
                    )
                  : '';
              <wcg-uni-select
                data-testid="single-select-dialog__select-field"
                class="w-full"
                [errorText]="data.errorText!"
                formControlName="selectControl"
                [required]="data.isRequired ?? false"
                [options]="data.options"
                [label]="data.label"
                [hintText]="hintText ?? ''"
              />
            }
          </div>
          <div tertiary>
            <wcg-button
              color="tertiary"
              data-testid="single-select-dialog__cancel-button"
              (click)="close()"
            >
              Cancel
            </wcg-button>
          </div>
          <div primary>
            @let dataTestId = 'single-select-dialog__' + data.buttonLabel;
            <wcg-button
              color="primary"
              [attr.data-testid]="dataTestId"
              [isDisabled]="!singleSelectForm.valid"
              (click)="save()"
            >
              {{ data.buttonLabel }}
            </wcg-button>
          </div>
        </wcg-dialog>
      </form>
    }
  `,
})
export class SingleSelectDialogComponent implements OnInit {
  private readonly _dialogRef = inject(
    MatDialogRef<SingleSelectDialogComponent>,
  );
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilds = inject(FormBuilder);
  public readonly data =
    inject<SingleSelectDialogData<WcgMenuOption>>(MAT_DIALOG_DATA);

  readonly organizationRoleHintMapper = organizationRoleHints;
  singleSelectForm = this._createForm();
  constructor() {
    this._customListeners();
  }

  ngOnInit() {
    this._setSelectControlValidators();
    switch (this.data.mode) {
      case DIALOG_MODES.EDIT:
        this.singleSelectForm.patchValue({
          selectControl: this.data.data,
        });
    }
  }

  save(): void {
    this.data.callbacks.saveData(
      this.singleSelectForm.controls.selectControl.value as WcgMenuOption,
    );
  }
  close(): void {
    this._dialogRef.close();
  }

  private _createForm() {
    return this._formBuilds.group({
      selectControl: new FormControl<WcgMenuOption | null>(null),
    });
  }

  private _setSelectControlValidators(): void {
    setFormControlValidators(
      this.singleSelectForm.controls.selectControl,
      this.data.validatorFn,
    );
  }
  private _customListeners(): void {
    toObservable(this.data.$canCloseDialog)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (canClose: boolean) => {
          if (canClose) {
            this._dialogRef.close();
          }
        },
      });
  }
}
